// ============================================
// LIGA AJUPAM - Match Opener Auth Service (v6.8.0)
// ============================================
// Wrappers de los 3 RPCs auth + match_opener_get_live_courts +
// match_opener_get_match_pending_pushes + opener_get_match_result_log.

import { supabase, handleSupabaseError } from './client';
import { withTimeout } from '../../utils/withTimeout';
import { staffAuthError } from '../../utils/staffSession';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const callRpc = async (fnName: string, params: Record<string, any>): Promise<any> => {
  // Timeout: validateOpenerSession alimenta el loader "Validando sesión…" del
  // centro de control; un RPC colgado (WiFi débil de cancha) lo dejaba infinito.
  const { data, error } = await withTimeout<any>((supabase.rpc as any)(fnName, params), 15000, fnName);
  if (error) throw handleSupabaseError(error);
  return data;
};

// =============================================================================
// AUTH
// =============================================================================

export interface OpenerLoginResult {
  session_token: string;
  opener_id: string;
  display_name: string;
  league_id: string;
  league_slug: string;
  league_name: string;
  expires_at: string;
  user_type: string;
  default_venue_id: string | null;
  venue_ids: string[];
  venue_names: string[];
}

/**
 * Login del juez de cancha. RAISE 28000 si credenciales inválidas/cuenta vencida/liga inactiva.
 *
 * @param leagueSlug Opcional. Si NULL, requiere que haya 1 sola liga 'enabled'.
 */
export const openerLogin = async (
  username: string,
  pin: string,
  leagueSlug?: string | null,
  userAgent?: string | null,
): Promise<OpenerLoginResult> => {
  const data = await callRpc('match_opener_login', {
    p_username: username,
    p_pin: pin,
    p_league_slug: leagueSlug ?? null,
    p_user_agent: userAgent ?? (typeof navigator !== 'undefined' ? navigator.userAgent : null),
  });
  if (!Array.isArray(data) || data.length === 0) {
    throw new Error('Login: respuesta vacía');
  }
  return data[0] as OpenerLoginResult;
};

export interface OpenerSessionInfo {
  opener_id: string;
  league_id: string;
  display_name: string;
  session_id: string;
}

/**
 * Valida un token guardado en localStorage al cargar la app.
 * RAISE 28000 si inválido/expirado.
 */
export const validateOpenerSession = async (token: string): Promise<OpenerSessionInfo> => {
  const data = await callRpc('match_opener_validate_session', { p_token: token });
  if (!Array.isArray(data) || data.length === 0) {
    throw staffAuthError();
  }
  return data[0] as OpenerSessionInfo;
};

/**
 * Logout idempotente — no hace RAISE si el token no existe.
 */
export const openerLogout = async (token: string): Promise<void> => {
  await callRpc('match_opener_logout', { p_token: token });
};

// =============================================================================
// LIVE COURTS (centro de control)
// =============================================================================

/**
 * Estado computado por la VIEW v_finals_court_status.
 *  - EMPTY: sin partido asignado al slot.
 *  - SCHEDULED: partido programado, sin abrir wizard.
 *  - OPENING: wizard arrancó pero peloteo no inició.
 *  - WARMUP: peloteo en curso (countdown corriendo).
 *  - PLAYING: peloteo cerrado, partido en juego.
 *  - FINISHED: partido finalizado.
 *  - CLOSED: WO/RET/CANCELADO.
 *  - UNKNOWN: transición rara (defensa).
 */
export type CourtState =
  | 'EMPTY' | 'SCHEDULED' | 'OPENING' | 'WARMUP'
  | 'PLAYING' | 'FINISHED' | 'CLOSED' | 'UNKNOWN';

export interface LiveCourt {
  slot_id: string;
  venue_id: string | null;
  venue_name: string | null;
  court_number: number;
  slot_date: string;
  slot_time: string;
  finals_match_id: string | null;
  match_status: string | null;
  match_phase: string | null;
  cup_type: string | null;
  bracket_label: string | null;
  match_code: string | null;
  team_a_qualified_id: string | null;
  team_b_qualified_id: string | null;
  team_a_id: string | null;
  team_a_name: string | null;
  team_b_id: string | null;
  team_b_name: string | null;
  team_a_sets: number | null;
  team_b_sets: number | null;
  result_set1_a: number | null;
  result_set1_b: number | null;
  result_set2_a: number | null;
  result_set2_b: number | null;
  result_set3_a: number | null;
  result_set3_b: number | null;
  is_super_tie_break: boolean | null;
  scheduled_date: string | null;
  match_started_at: string | null;
  photo_evidence_url: string | null;
  match_updated_at: string | null;
  opening_opener_id: string | null;
  opening_started_at: string | null;
  warmup_started_at: string | null;
  warmup_ends_at: string | null;
  warmup_ended_at: string | null;
  steps_completed: string[] | null;
  coin_result: string | null;
  coin_choice_text: string | null;
  initial_photo_url: string | null;
  opening_cancelled_at: string | null;
  court_state: CourtState;
  league_id: string;
  category_id: string | null;
  category_name: string | null;
  category_branch: string | null;
  // v6.9.1 hotfix #22: profiles de los 4 jugadores (captain + partner por equipo)
  team_a_captain_name: string | null;
  team_a_captain_photo: string | null;
  team_a_captain_nickname: string | null;
  team_a_partner_name: string | null;
  team_a_partner_photo: string | null;
  team_a_partner_nickname: string | null;
  team_b_captain_name: string | null;
  team_b_captain_photo: string | null;
  team_b_captain_nickname: string | null;
  team_b_partner_name: string | null;
  team_b_partner_photo: string | null;
  team_b_partner_nickname: string | null;
  // v6.9.4: presencia marcada por recepcionista
  reception_team_a_present: boolean;
  reception_team_b_present: boolean;
  // v6.9.6: zona/posición de fase regular para RegularPhasePositionBadge
  position_a_zone: string | null;
  position_a_rank: number | null;
  position_b_zone: string | null;
  position_b_rank: number | null;
  // v6.9.7.07: descripciones TBD ("Ganador de R8", "5° Zona A") cuando el slot
  // todavía no tiene equipo definido. Provisto por la VIEW v_finals_court_status.
  tbd_description_a: string | null;
  tbd_description_b: string | null;
  // v6.10.0.20: fin físico anticipado de la cancha (resultado pendiente). Cuando
  // está seteado, el partido ya no cuenta como ocupante de la cancha y se puede
  // abrir el siguiente aunque este siga EN_CURSO sin resultado cargado.
  court_released_at: string | null;
  /** v6.11.209 — última vez que se tocó el marcador (o "sin cambios"). */
  score_updated_at?: string | null;
}

export const getOpenerLiveCourts = async (
  token: string,
  categoryId?: string | null,
  targetDate?: string | null, // YYYY-MM-DD; si null, server usa today ART
): Promise<LiveCourt[]> => {
  const data = await callRpc('match_opener_get_live_courts', {
    p_opener_session_token: token,
    p_category_id: categoryId ?? null,
    p_target_date: targetDate ?? null,
  });
  return data || [];
};

// v6.9.6: paridad con reception — próximo día con partidos visibles para el juez de cancha
export const getOpenerNextMatchDate = async (token: string): Promise<string | null> => {
  const data = await callRpc('opener_get_next_match_date', { p_opener_session_token: token });
  if (!Array.isArray(data) || data.length === 0) return null;
  return data[0]?.next_date ?? null;
};

// =============================================================================
// EXTRAS: pending pushes + result log (utility para detalle del partido)
// =============================================================================

export interface MatchPendingPush {
  id: string;
  notification_type: 'WARMUP_WARNING' | 'WARMUP_END';
  scheduled_at: string;
  status: 'PENDING' | 'SENT' | 'FAILED' | 'CANCELLED';
  attempts: number;
  processed_at: string | null;
}

export const getMatchPendingPushes = async (
  token: string,
  finalsMatchId: string,
): Promise<MatchPendingPush[]> => {
  const data = await callRpc('match_opener_get_match_pending_pushes', {
    p_opener_session_token: token,
    p_finals_match_id: finalsMatchId,
  });
  return data || [];
};

export interface MatchResultLogEntry {
  id: string;
  actor_type: 'OPENER' | 'ADMIN';
  actor_name: string;
  result_after: Record<string, unknown>;
  is_final: boolean;
  notes: string | null;
  created_at: string;
}

export const getMatchResultLog = async (
  token: string,
  finalsMatchId: string,
): Promise<MatchResultLogEntry[]> => {
  const data = await callRpc('opener_get_match_result_log', {
    p_opener_session_token: token,
    p_finals_match_id: finalsMatchId,
  });
  return data || [];
};
